import * as React from 'react';

import { cn } from '../../lib/utils';

/**
 * Visual driver for the cell. Empty / Full / Error map directly to Figma
 * `289:49541` states; Focused is a code-only addition per a11y (Figma did
 * not publish a focus variant; design.md § 3.4 calls this out).
 */
export type NumberFieldState = 'empty' | 'full' | 'error' | 'focused';

export interface NumberFieldProps {
  /** Current single-digit value of the cell. Pass empty string for empty. */
  value: string;
  /** Callback fired when the digit changes. Receives '' (cleared) or a single digit. */
  onChange: (next: string) => void;
  /**
   * Canonical visual driver. Defaults to `'empty'` / `'full'` based on `value`
   * but callers (e.g., `CodeInput` orchestrating error or focus) override it.
   */
  state?: NumberFieldState;
  /** Imperative ref to the underlying input. Useful for focus orchestration. */
  inputRef?: React.RefObject<HTMLInputElement | null> | ((el: HTMLInputElement | null) => void);
  /** Disables the cell. */
  disabled?: boolean;
  /** Optional className appended to the cell wrapper. */
  className?: string;
  /** ARIA label for screen readers (e.g., "Code digit 1"). */
  'aria-label'?: string;
  /** Native key handler — `CodeInput` uses this for backspace / arrow nav. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Native paste handler — `CodeInput` uses this for full-code paste. */
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  /** Native focus handler. */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Native blur handler. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * NumberField atom — single-digit OTP cell per Figma `289:49541` and
 * design.md § 3.4. 40×40 square, padding 16, border radius 4. The `state`
 * prop is the canonical visual driver; `value` is the data driver.
 *
 * Promoted from `CodeInput`-private cell rendering so it can be reused
 * outside the OTP molecule (see TDEV-273 design decision #4).
 */
export const NumberField: React.FC<NumberFieldProps> = ({
  value,
  onChange,
  state,
  inputRef,
  disabled = false,
  className,
  'aria-label': ariaLabel,
  onKeyDown,
  onPaste,
  onFocus,
  onBlur,
}) => {
  // Derive state from value when not explicitly passed.
  const effectiveState: NumberFieldState = state ?? (value === '' ? 'empty' : 'full');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;

    // Allow clearing.
    if (next === '') {
      onChange('');
      return;
    }

    // Single digit only — accept the last character if numeric (handles paste-into-cell).
    const lastChar = next.slice(-1);
    if (/^\d$/.test(lastChar)) {
      onChange(lastChar);
    }
    // Non-numeric: silently reject (no onChange call).
  };

  const setRef = React.useCallback(
    (el: HTMLInputElement | null) => {
      if (typeof inputRef === 'function') {
        inputRef(el);
      } else if (inputRef && 'current' in inputRef) {
        (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
      }
    },
    [inputRef]
  );

  return (
    <div data-numberfield-cell className={cn('w-10 h-10', className)}>
      <input
        ref={setRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={1}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn(
          'h-full w-full rounded border bg-transparent text-center',
          'font-outfit text-[24px] font-medium leading-[1.2] text-dawn-black',
          'focus:outline-none focus:ring-2 focus:ring-dawn-orange',
          stateStyles[effectiveState],
          disabled && 'cursor-not-allowed opacity-50'
        )}
      />
    </div>
  );
};

NumberField.displayName = 'NumberField';

/** Per-state border classes per Figma `289:49541` + design.md § 3.4. */
const stateStyles: Record<NumberFieldState, string> = {
  empty: 'border-grey-400',
  full: 'border-grey-400',
  error: 'border-error',
  focused: 'border-dawn-orange ring-2 ring-dawn-orange',
};
